import { ColumnDef } from '@tanstack/react-table';
import {
  MultiTagValuesDisplay,
  TagValueDisplay,
} from 'src/components/TagsFormBody/TagValueCell';
import {
  COLUMN_MAX_SIZE,
  COLUMN_MIN_SIZE,
} from 'src/contexts/ColumnResizingContext/ColumnResizingContext.types';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { FormatOptionEntries } from 'src/modals/EditTableColumns';
import { CustomNumericCell } from 'src/tables/ListingTable/configs/CustomNumericCell';
import { TableShimmeringDiv } from 'src/tables/Table';
import {
  getTagColumnAccessorValue,
  getTagValuesForKey,
  isNumericTagValueType,
} from 'src/utils/tagUtils';
import {
  SECTION_TYPE_TO_PRECISION_SETTING,
  SectionType,
} from 'src/utils/types/sectionType';

import { SHIMMERING_DIV_HEIGHT_SINGLE_SALE } from '../../SalesTable/SalesTable.type';

interface TagColumnOptions<TData> {
  /** Tag name (used as column ID) */
  tagName?: string;
  /** Column ID (alternative to tagName) */
  columnId?: string;
  /** Section type for precision settings */
  sectionType?: SectionType;
  /** Accessor function to get tags array from data */
  tagsAccessor: (data: TData) => any[] | null | undefined;
  /** Accessor function to get currency code from data */
  currencyAccessor: (data: TData) => string | undefined;
  /** Whether sorting is enabled for this column. Pass false in Search V2 contexts
   * where sorting is server-side and tag field names are not registered in the
   * backend sort mapper. */
  enableSorting?: boolean;
  /** When true, non-numeric multi-value tags render every value (comma separated).
   * Numeric tags (Int/Decimal) always keep showing only the first value.
   * Gated by the MultiValueTagsInColumns feature flag at the call site. */
  hasMultiValueTagsInColumns?: boolean;
}

/**
 * Factory function to create tag column definitions for tables.
 * Handles both numeric (Int/Decimal) and non-numeric tag values.
 *
 * @example
 * createTagColumn({
 *   tagName: 'CustomTag',
 *   sectionType: SectionType.Sales,
 *   tagsAccessor: (data) => data?.sale?.tags,
 *   currencyAccessor: (data) => data?.sale?.currency,
 * })
 */
export const createTagColumn = <TData,>(
  options: TagColumnOptions<TData>
): ColumnDef<TData> => {
  const {
    tagName,
    columnId,
    sectionType = SectionType.Sales,
    tagsAccessor,
    currencyAccessor,
    enableSorting,
    hasMultiValueTagsInColumns = false,
  } = options;

  const id = columnId ?? tagName;
  if (!id) {
    throw new Error('Either tagName or columnId must be provided');
  }

  return {
    id,
    ...(enableSorting !== undefined && { enableSorting }),
    header: () => (
      <TableHeaderCellDiv align="right">
        <span style={{ overflowX: 'clip', textOverflow: 'ellipsis' }}>
          {id}
        </span>
      </TableHeaderCellDiv>
    ),
    enableColumnFilter: false,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    accessorFn: (data) =>
      getTagColumnAccessorValue(
        tagsAccessor(data),
        id,
        hasMultiValueTagsInColumns
      ),
    cell: function Cell({ getValue, row: { original }, table }) {
      const { value: storedColumnNumberPrecisions = {} } =
        useServerUserSetting<FormatOptionEntries>({
          id: SECTION_TYPE_TO_PRECISION_SETTING[
            table.options.meta?.sectionType ?? sectionType
          ],
        });
      const tags = tagsAccessor(original);
      const matchingTags = getTagValuesForKey(tags, id);
      const tag = matchingTags[0];

      if (isNumericTagValueType(tag?.valueType)) {
        const userDefinedPrecision = storedColumnNumberPrecisions[id];

        return (
          <CustomNumericCell
            columnId={id}
            userDefinedPrecision={userDefinedPrecision}
            value={getValue<number | null>()}
            isLoading={Boolean(!original || !tags)}
            shimmeringDivHeight={SHIMMERING_DIV_HEIGHT_SINGLE_SALE}
            sectionType={table.options.meta?.sectionType ?? sectionType}
            matchingTag={tag}
            currencyCode={currencyAccessor(original)}
          />
        );
      }

      if (!original || !tags) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      if (!tag) {
        return null;
      }

      return (
        <TableCellDiv
          align="right"
          // Disable the cell tooltip for multi-value tags; it conflicts with the per-value hover tooltips.
          showTooltip={!(hasMultiValueTagsInColumns && matchingTags.length > 1)}
        >
          {hasMultiValueTagsInColumns && matchingTags.length > 1 ? (
            <MultiTagValuesDisplay tags={matchingTags} displayMode />
          ) : (
            <TagValueDisplay tag={tag} displayMode />
          )}
        </TableCellDiv>
      );
    },
  };
};
